/* Single choke point for every /gw API request so the Supabase access token is
 * never forgotten. Reads the token fresh from getSession() (supabase-js refreshes
 * it automatically, so we must not cache it) and attaches it as a Bearer header.
 *
 * `path` is everything AFTER /gw, e.g. gwFetch("/restaurants?limit=100") or
 * gwFetch("/menu_items/42", { method: "DELETE" }). Public reads work with or
 * without a token; sending it when present is harmless and future-proof. */
import { supabase } from "../lib/supabase";
import { GW_API_BASE_URL } from "../config";

/* A failed /gw request, carrying the HTTP status so callers can distinguish the
   auth cases the role system introduced: 401 (not signed in) vs 403 (signed in
   but not permitted). Throw it from the api/* mappers instead of a bare Error. */
export class GwApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = "GwApiError";
    this.status = status;
  }
}

/* Friendly, user-facing message for a failed /gw request. 401 means the caller
   isn't signed in; 403 means they're signed in but lack permission (e.g. an
   owner editing a restaurant they don't own, or a non-admin creating/deleting).
   Any other status keeps the caller's technical fallback. */
export function apiErrorMessage(status: number, fallback: string): string {
  if (status === 401) return "Please sign in to continue.";
  if (status === 403) return "You don't have permission to do that.";
  return fallback;
}

export async function gwFetch(
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  const headers = new Headers(init.headers);
  if (session?.access_token) {
    headers.set("Authorization", `Bearer ${session.access_token}`);
  }

  const res = await fetch(`${GW_API_BASE_URL}/gw${path}`, { ...init, headers });

  // A 401 means the token is missing/expired/invalid. Clear the stale session so
  // the app reflects "logged out" (AccountControl updates via onAuthStateChange);
  // still return the response so callers surface their own error handling.
  if (res.status === 401 && session) {
    await supabase.auth.signOut();
  }

  return res;
}
